// React Imports
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

// External Libraries
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Password } from "primereact/password";
import { InputSwitch } from "primereact/inputswitch";
import { Card } from "primereact/card";

// Local Components
import { LoginLayout } from "../LoginLayout/LoginLayout";

// Hooks & Helpers
import { getAuthClient, getLoginClient } from "sj-ui-toolkit/src/client";
import {
  savePathForRedirect,
  rehydrateUserAuthStatus,
  getCurrentSelectedTenant,
} from "sj-ui-toolkit/src/utils";
import { useDebounce, useLoginFlow } from "sj-ui-toolkit/src/hooks";
import { themeClasses as T } from "sj-ui-toolkit/src/atlas/utils/themeClasses";

// Styles
import S from "./LoginPage.module.scss";

// Constants
import { REMEMBER_EMAIL_KEY, USE_PASSWORD_KEY } from "../../../constants/login";
import { LoginAppRoutes } from "sj-ui-toolkit/src/constants/routes";

const EmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FORCE_PASSWORD_AUTH_SWITCH_ID = "force-password-auth";

interface LoginPageProps {
  signedOut?: boolean;
}

export default function LoginPage({ signedOut = false }: LoginPageProps) {
  const authClient = getAuthClient();
  const loginClient = getLoginClient();
  const [email, setEmail] = useState(localStorage.getItem(REMEMBER_EMAIL_KEY) || "");
  const idpSamlUrlRef = useRef<string | null>(null);
  const [password, setPassword] = useState("");
  const [passwordLogin, setPasswordLogin] = useState(false);
  const [forcePasswordAuth, setForcePasswordAuth] = useState(
    localStorage.getItem(USE_PASSWORD_KEY) === "true"
  );
  const forcePasswordAuthRef = useRef<boolean>(false);
  const [idpCheckInProgress, setIdpCheckInProgress] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(!!localStorage.getItem(REMEMBER_EMAIL_KEY));
  const pendingSubmitRef = useRef<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get("redirect");
  const domain = searchParams.get("domain");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { handlePostLogin } = useLoginFlow();

  // set up a debounced email value to avoid excessive calls
  const debouncedEmail = useDebounce(email, 300);

  // Check if user is already logged in
  useEffect(() => {
    const checkAuthStatus = async () => {
      const isAuthenticated = await rehydrateUserAuthStatus();

      if (isAuthenticated) {
        const currentTenant = getCurrentSelectedTenant();
        if (currentTenant) {
          // User is logged in with tenant selected - redirect to home
          window.location.href = "/";
        } else {
          // User is logged in but no tenant selected - redirect to tenant selection
          navigate(LoginAppRoutes.routes.tenantSelection.route);
        }
      }
    };
    checkAuthStatus();
  }, [navigate]);

  const onEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value.trim();
    setEmail(email);
  };

  const handleForcePasswordAuthChange = (value: boolean) => {
    // Hide password input immediately when toggling off force mode.
    if (!value) {
      setPasswordLogin(false);
      idpSamlUrlRef.current = null;
      pendingSubmitRef.current = false;
      setIsSubmitting(false);
    }

    setForcePasswordAuth(value);
  };

  useEffect(() => {
    forcePasswordAuthRef.current = forcePasswordAuth;
    localStorage.setItem(USE_PASSWORD_KEY, String(forcePasswordAuth));

    if (forcePasswordAuth) {
      idpSamlUrlRef.current = null;
      setPasswordLogin(true);
      pendingSubmitRef.current = false;
      setIsSubmitting(false);
    }
  }, [forcePasswordAuth]);

  // whenever the debounced email changes, check for IdP URL
  useEffect(() => {
    if (!domain && !forcePasswordAuth && debouncedEmail && EmailRegex.test(debouncedEmail)) {
      checkForIdpUrl(debouncedEmail);
    }
  }, [debouncedEmail, domain, forcePasswordAuth]);

  useEffect(() => {
    if (!domain) return;

    const redirectUsingDomain = async (domain: string) => {
      try {
        const response = await loginClient.redirect({
          domain,
          redirect_to: `${window.location.origin}${LoginAppRoutes.routes.samlRedirect.route}`,
        });

        if (response.data?.url) {
          savePathForRedirect(redirect ?? undefined);
          window.location.href = response.data.url;
        }
      } catch (error) {
        console.error("Error redirecting using domain:", error);
      }
    };

    redirectUsingDomain(domain);
  }, [domain]);

  const checkForIdpUrl = async (email: string) => {
    setIdpCheckInProgress(true);

    try {
      const response = await loginClient.redirect({
        email,
        redirect_to: `${window.location.origin}${LoginAppRoutes.routes.samlRedirect.route}`,
      });

      if (forcePasswordAuthRef.current) {
        return;
      }

      if (response.data?.url) {
        idpSamlUrlRef.current = response.data.url;
        setPasswordLogin(false);

        // If user already submitted the form, auto-redirect for SAML
        if (pendingSubmitRef.current) {
          pendingSubmitRef.current = false;
          handleSamlSignIn();
        }
      } else {
        idpSamlUrlRef.current = null;
        setPasswordLogin(true);
        // Reset pending flag - user needs to enter password and resubmit
        pendingSubmitRef.current = false;
        setIsSubmitting(false);
      }
    } catch (error) {
      console.error("Error checking for IdP URL:", error);
      // Fall back to password login so the UI doesn't get stuck disabled/loading
      idpSamlUrlRef.current = null;
      setPasswordLogin(true);
      pendingSubmitRef.current = false;
      setIsSubmitting(false);
    } finally {
      setIdpCheckInProgress(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Store email preference
    if (rememberEmail && EmailRegex.test(email)) {
      localStorage.setItem(REMEMBER_EMAIL_KEY, email);
    } else {
      localStorage.removeItem(REMEMBER_EMAIL_KEY);
    }

    // If IDP check is still in progress, mark as pending and wait for auto-redirect
    if (!forcePasswordAuth && (idpCheckInProgress || email !== debouncedEmail)) {
      pendingSubmitRef.current = true;
      setIsSubmitting(true);
      return;
    }

    setIsSubmitting(true);
    try {
      if (!forcePasswordAuth && !passwordLogin) {
        await handleSamlSignIn();
      } else {
        await handlePasswordSignIn();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSamlSignIn = async () => {
    savePathForRedirect(redirect ?? undefined);
    const url = idpSamlUrlRef.current;
    if (url) {
      window.location.href = url;
    }
  };

  const handlePasswordSignIn = async () => {
    savePathForRedirect(redirect ?? undefined);
    const nonceResponse = await loginClient.generateAuthNonce();

    if (nonceResponse.error || !nonceResponse.data?.nonce) {
      console.error("Error generating auth nonce:", nonceResponse.error);
      return;
    }

    const authResponse = await loginClient.passwordAuthenticate({
      username: email,
      password,
      nonce: nonceResponse.data.nonce,
    });

    if (authResponse.error || !authResponse.data?.id_token) {
      window.toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: authResponse.error?.message || "Error during authentication",
        life: 5000,
      });
      return;
    }

    const { data, error } = await authClient.exchangeForAccessToken({});
    if (error) {
      window.toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Error exchanging token",
        life: 5000,
      });
      return;
    }

    if (!data?.access_token) {
      window.toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No access token returned",
        life: 5000,
      });
      return;
    }

    await handlePostLogin(data.access_token, false);
  };

  return (
    <LoginLayout title={"Login"}>
      <form className={`p-fluid ${S.root}`} onSubmit={handleSignIn} data-testid="login-form">
        {signedOut && (
          <>
            <Card className={T.bgElevation2}>
              <h2 className="atlas-h4">You have been signed out</h2>
              <p>Please log in again to continue.</p>
            </Card>
          </>
        )}
        <InputText
          id="email"
          data-testid="login-email-input"
          className="w-full"
          type="email"
          name="email"
          value={email}
          onChange={onEmailChange}
          placeholder="Email address"
          autoComplete="email"
          required
          aria-required="true"
          aria-label="Email address"
        />

        {(forcePasswordAuth || passwordLogin) && (
          <div className="w-full">
            <Password
              id="password"
              data-testid="login-password-input"
              className="w-full"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Password"
              required
              aria-required="true"
              aria-label="Password"
              toggleMask
              feedback={false}
              pt={{
                hideIcon: { className: S.pwIcon },
                showIcon: { className: S.pwIcon },
                input: { className: S.passwordInput },
                root: { className: S.passwordInput },
              }}
            />
            <div className={S.forgotPasswordContainer}>
              <Button
                className={S.forgotPasswordButton}
                data-testid="login-forgot-password-button"
                link
                type="button"
                onClick={() => navigate(LoginAppRoutes.routes.forgotPassword.route)}
                label="Forgot Password?"
              />
            </div>
          </div>
        )}

        <div className={S.checkboxContainer}>
          <InputSwitch
            inputId={FORCE_PASSWORD_AUTH_SWITCH_ID}
            checked={forcePasswordAuth}
            onChange={e => handleForcePasswordAuthChange(e.value)}
            pt={{
              root: { role: "presentation", "aria-checked": undefined },
              input: { "data-testid": "login-force-password-switch" },
            }}
          />
          <label htmlFor={FORCE_PASSWORD_AUTH_SWITCH_ID} className="ml-2">
            Use Password
          </label>
        </div>

        <div className={S.checkboxContainer}>
          <InputSwitch
            inputId={REMEMBER_EMAIL_KEY}
            checked={rememberEmail}
            onChange={e => setRememberEmail(e.value)}
            // Prime React: Root div has role="checkbox" nesting the inner
            // input role="switch". Override to "presentation" to fix a11y violation
            pt={{
              root: { role: "presentation", "aria-checked": undefined },
              input: { "data-testid": "login-remember-me-switch" },
            }}
          />
          <label htmlFor={REMEMBER_EMAIL_KEY} className="ml-2">
            Remember Me
          </label>
        </div>
        <Button
          className="w-full"
          data-testid="login-submit-button"
          type="submit"
          label="Sign In"
          disabled={isSubmitting}
          loading={isSubmitting}
        />
        <Button
          className={`${S.actionButton} w-full`}
          data-testid="login-register-button"
          severity="secondary"
          type="button"
          onClick={() => navigate(LoginAppRoutes.routes.register.route)}
          label="New Learner? Register here"
        />
      </form>
    </LoginLayout>
  );
}
